import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { monitoringAPI } from '../services/api';
import { useAuthStore } from '../stores/authStore';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

export function MonitoringPage() {
  const navigate = useNavigate();
  const isSuperuser = useAuthStore((s) => s.user?.is_superuser);

  const { data: overviewData, isLoading: overviewLoading } = useQuery({
    queryKey: ['monitoring', 'overview'],
    queryFn: () => monitoringAPI.overview(),
  });

  const { data: dailyData } = useQuery({
    queryKey: ['monitoring', 'daily-usage', 7],
    queryFn: () => monitoringAPI.dailyUsage(7),
  });

  const { data: tenantData } = useQuery({
    queryKey: ['monitoring', 'tenant-usage'],
    queryFn: () => monitoringAPI.tenantUsage(),
  });

  const overview = overviewData?.data;
  const daily = dailyData?.data || [];
  const tenants = tenantData?.data || [];

  // [2026-03-24] 예상 비용은 슈퍼관리자만 표시
  const stats = [
    { label: '총 대화', value: overview?.total_conversations || 0, color: 'bg-blue-500' },
    { label: '총 메시지', value: overview?.total_messages || 0, color: 'bg-green-500' },
    { label: '총 토큰', value: (overview?.total_tokens || 0).toLocaleString(), color: 'bg-purple-500' },
    ...(isSuperuser ? [{ label: '예상 비용', value: `$${overview?.estimated_cost_usd || '0.00'}`, color: 'bg-orange-500' }] : []),
  ];

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">모니터링</h1>
        <p className="text-gray-600">API 사용량 및 시스템 현황을 모니터링합니다.</p>
      </div>

      {/* Stats Grid */}
      <div className={`grid grid-cols-1 md:grid-cols-2 ${isSuperuser ? 'lg:grid-cols-4' : 'lg:grid-cols-3'} gap-6 mb-8`}>
        {stats.map((stat) => (
          <div key={stat.label} className="bg-white rounded-lg shadow p-6">
            <p className="text-sm text-gray-600">{stat.label}</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">
              {overviewLoading ? '...' : stat.value}
            </p>
          </div>
        ))}
      </div>

      {/* 최근 7일 사용량 차트 */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900">최근 7일 사용량</h2>
          <button
            onClick={() => navigate('/monitoring/tokens')}
            className="text-sm text-blue-600 hover:underline"
          >
            상세 보기
          </button>
        </div>
        <div className="h-64">
          {daily.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={daily}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tickFormatter={(v: string) => v.slice(5)} />
                <YAxis />
                <Tooltip />
                <Bar dataKey="messages" name="메시지" fill="#3B82F6" />
                <Bar dataKey="conversations" name="대화" fill="#10B981" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full flex items-center justify-center text-gray-400">
              데이터가 없습니다.
            </div>
          )}
        </div>
      </div>

      {/* 테넌트별 사용량 요약 */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-lg font-semibold text-gray-900">테넌트별 사용량</h2>
          <button
            onClick={() => navigate('/monitoring/tenants')}
            className="text-sm text-blue-600 hover:underline"
          >
            상세 보기
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">테넌트</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">대화</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">메시지</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">토큰</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {tenants.slice(0, 5).map((t: any) => (
                <tr key={t.tenant_id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-medium text-gray-900">{t.tenant_name}</td>
                  <td className="px-6 py-4 text-gray-600">{t.conversations}</td>
                  <td className="px-6 py-4 text-gray-600">{t.messages}</td>
                  <td className="px-6 py-4 text-gray-600">{t.total_tokens.toLocaleString()}</td>
                </tr>
              ))}
              {tenants.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-6 py-8 text-center text-gray-400">데이터가 없습니다.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
